import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  ConnectionState,
  Room,
  RoomEvent,
  Track,
  type LocalAudioTrack,
  type LocalParticipant,
  type LocalVideoTrack,
  type RemoteParticipant,
  type VideoCodec,
} from 'livekit-client';

import type { JoinIntent } from '../App';
import {
  CHAT_TOPIC,
  ROOM_UPDATE_EVENTS,
  collectParticipants,
  SCREEN_SHARE_OPTIONS,
  createRoom,
  decodeChatMessage,
  readRoomSettings,
  screenShareHint,
  sendChatMessage,
  videoPublishOptions,
  type ChatMessage,
  type ParticipantView,
} from './livekit';
import { detectSupportedCodecs, resolveCodec, sampleLink, type LinkSample } from './codec';
import {
  AudioAdvisor,
  audioPresetFor,
  liveConstraints,
  type AudioMode,
} from './audio';
import { distributeBudget, videoProfileFor, type VideoMode } from './video';
import { describeDeviceError, type DeviceName } from './deviceErrors';
import { api } from './api';

/** Как часто снимается статистика связи в автоматическом режиме. */
const SAMPLE_INTERVAL_MS = 3000;

/** Пауза между двумя первыми пробами при открытии панели качества. */
const WARMUP_INTERVAL_MS = 900;

export type LeaveReason = 'user' | 'error';

interface Options {
  intent: JoinIntent;
  onLeave: (reason: LeaveReason, message?: string) => void;
  /**
   * Открыта ли панель качества.
   *
   * Пока она закрыта, цифры никто не видит, и обновлять их в состоянии
   * React незачем: каждое обновление перерисовывало всю сетку видео раз
   * в три секунды. На телефоне это чувствовалось как подтормаживание.
   */
  statsVisible: boolean;
}

export interface CodecStatus {
  /** Кодек, которым публикуется видео. Выбирается один раз при входе. */
  active: VideoCodec;
  /** Был ли кодек подобран автоматически или задан участником вручную. */
  auto: boolean;
  /** Последняя проба связи — показывается в интерфейсе. */
  sample: LinkSample | null;
}

export interface VideoStatus {
  mode: VideoMode;
  /** Фактический потолок битрейта видео, бит/с. */
  maxBitrate: number;
}

export interface AudioStatus {
  mode: AudioMode;
  noiseSuppression: boolean;
  /** Фактический потолок битрейта звука, бит/с. */
  bitrate: number;
  lastChange: string;
}

export interface RoomConnection {
  room: Room | null;
  state: ConnectionState;
  participants: ParticipantView[];
  chat: ChatMessage[];
  unreadChat: number;
  error: string;
  /** Подсказка без тревоги: работать можно, но лучше поправить. */
  notice: string;
  dismissNotice: () => void;
  startedAt: number | null;
  codec: CodecStatus;
  video: VideoStatus;
  audio: AudioStatus;

  micEnabled: boolean;
  cameraEnabled: boolean;
  screenShareEnabled: boolean;

  /** Организатор ли участник: от этого зависят запись и режим презентации. */
  isHost: boolean;
  /** Включён ли режим презентации — величина общая для всей комнаты. */
  presentation: boolean;

  toggleMic: () => Promise<void>;
  toggleCamera: () => Promise<void>;
  toggleScreenShare: () => Promise<void>;
  switchDevice: (kind: MediaDeviceKind, deviceId: string) => Promise<void>;
  setVideoMode: (mode: VideoMode) => Promise<void>;
  setAudioMode: (mode: AudioMode) => Promise<void>;
  setNoiseSuppression: (enabled: boolean) => Promise<void>;
  setPresentation: (on: boolean) => Promise<void>;
  sendMessage: (text: string) => Promise<void>;
  markChatRead: () => void;
  leave: () => Promise<void>;
}

export function useRoomConnection({ intent, onLeave, statsVisible }: Options): RoomConnection {
  const [room, setRoom] = useState<Room | null>(null);

  // Любое событие комнаты перерисовывает интерфейс: состояние читается
  // напрямую из объекта Room, поэтому рассинхрона быть не может.
  // Счётчик заодно служит ключом мемоизации списка участников.
  const [version, bump] = useReducer((n: number) => n + 1, 0);

  const [state, setState] = useState<ConnectionState>(ConnectionState.Connecting);
  const [chat, setChat] = useState<ChatMessage[]>([]);
  const [unreadChat, setUnreadChat] = useState(0);
  const [error, setError] = useState('');
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [presentation, setPresentationState] = useState(false);
  /** Спокойное сообщение: не ошибка, а предупреждение о том, что стоит сделать иначе. */
  const [notice, setNotice] = useState('');

  // Набор кодеков, которые этот браузер способен кодировать, — величина
  // постоянная, спрашиваем один раз.
  const supportedCodecs = useMemo(detectSupportedCodecs, []);
  const initialCodec = useMemo(
    () => resolveCodec(intent.codec, supportedCodecs),
    [intent.codec, supportedCodecs],
  );

  const [linkSample, setLinkSample] = useState<LinkSample | null>(null);

  /**
   * Состояние устройств, которое человек только что запросил нажатием.
   *
   * Живёт от нажатия до момента, когда SDK закончит работу. Пока ключа
   * здесь нет — показываем настоящее состояние из комнаты.
   */
  const [pending, setPending] = useState<{ mic?: boolean; camera?: boolean }>({});

  const [videoMode, setVideoModeState] = useState<VideoMode>(intent.videoMode);

  const [audioMode, setAudioModeState] = useState<AudioMode>(intent.audioMode);
  const [noiseSuppression, setNoiseSuppressionState] = useState(intent.noiseSuppression);
  const [audioBitrate, setAudioBitrate] = useState(
    () => audioPresetFor(intent.audioMode).maxBitrate ?? 24_000,
  );
  const [audioChange, setAudioChange] = useState('');

  // onLeave меняется между рендерами — держим свежую ссылку, чтобы это
  // не переподключало комнату.
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  // Взводится перед отключением, которое инициируем мы сами.
  const closing = useRef(false);

  // Советчик по звуку — один на встречу, чтобы пауза между изменениями
  // считалась сквозь переподключения.
  const audioAdvisorRef = useRef<AudioAdvisor | null>(null);
  // Флаг в ref, чтобы смена режима не пересоздавала цикл измерений.
  const audioAutoRef = useRef(intent.audioMode === 'auto');
  // Актуальные настройки для восстановления связи. В ref, а не в замыкании:
  // иначе смена режима пересоздавала бы подключение к комнате.
  const videoModeRef = useRef(intent.videoMode);
  const audioBitrateRef = useRef(audioPresetFor(intent.audioMode).maxBitrate ?? 24_000);

  /**
   * Единственный эффект владеет жизненным циклом комнаты целиком.
   *
   * Экземпляр Room создаётся здесь, а не хранится между попытками:
   * `disconnect()` асинхронный, и переиспользование одного объекта
   * приводило к тому, что отмена предыдущего подключения обрывала
   * следующее (React StrictMode монтирует эффекты дважды, но так же
   * ведёт себя и быстрый повторный вход).
   */
  useEffect(() => {
    const activeRoom = createRoom({
      cameraId: intent.cameraId,
      micId: intent.micId,
      videoCodec: initialCodec,
      videoMode: intent.videoMode,
      audioMode: intent.audioMode,
      noiseSuppression: intent.noiseSuppression,
    });
    let cancelled = false;
    closing.current = false;

    const update = () => bump();
    ROOM_UPDATE_EVENTS.forEach((event) => activeRoom.on(event, update));

    const onStateChanged = (next: ConnectionState) => {
      setState(next);
      if (next === ConnectionState.Connected) setStartedAt((at) => at ?? Date.now());
    };

    const onData = (
      payload: Uint8Array,
      participant?: RemoteParticipant,
      _kind?: unknown,
      topic?: string,
    ) => {
      if (topic !== CHAT_TOPIC) return;
      const message = decodeChatMessage(payload, participant);
      if (!message) return;
      setChat((history) => [...history, message]);
      setUnreadChat((n) => n + 1);
    };

    const onDisconnected = () => {
      if (closing.current) return;
      onLeaveRef.current('error', 'Соединение с встречей прервано');
    };

    /*
     * После переподключения настройки надо назначить заново.
     *
     * Смена сети — включили или выключили VPN, ушли с Wi-Fi в мобильный
     * интернет — заставляет LiveKit пересобрать соединение и опубликовать
     * дорожки заново. Слои и потолок видео при этом берутся из умолчаний
     * комнаты и приезжают правильными, а вот битрейт звука жил только
     * в параметрах прежнего отправителя и терялся вместе с ним.
     *
     * Отсюда и ощущение, что после VPN «ничего не переключается»: часть
     * настроек молча возвращалась к исходным. Теперь они назначаются
     * заново на каждое восстановление связи.
     */
    const onReconnected = () => {
      void applyAudioBitrate(activeRoom, audioBitrateRef.current);
      void applyVideoProfile(activeRoom, videoModeRef.current, initialCodec);
    };

    /*
     * Режим презентации живёт в метаданных комнаты.
     *
     * Так его видят все одинаково и, главное, сразу: подключившийся
     * позже читает метаданные при входе и попадает в тот же режим, что
     * и остальные, — а не остаётся в обычной сетке.
     */
    const onRoomMetadata = (metadata: string | undefined) => {
      setPresentationState(readRoomSettings(metadata).presentation);
    };

    activeRoom.on(RoomEvent.ConnectionStateChanged, onStateChanged);
    activeRoom.on(RoomEvent.DataReceived, onData);
    activeRoom.on(RoomEvent.Disconnected, onDisconnected);
    activeRoom.on(RoomEvent.Reconnected, onReconnected);
    activeRoom.on(RoomEvent.RoomMetadataChanged, onRoomMetadata);

    setRoom(activeRoom);

    (async () => {
      // Не удалось подключиться к медиасерверу — это фатально.
      try {
        await activeRoom.connect(intent.credentials.wsUrl, intent.credentials.token);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof Error ? err.message : 'Не удалось подключиться к медиасерверу';
        setError(message);
        onLeaveRef.current('error', message);
        return;
      }
      if (cancelled) return;

      // Режим, установленный до нашего прихода, применяем сразу.
      onRoomMetadata(activeRoom.metadata);

      // А вот отказ в доступе к камере или микрофону — штатная ситуация:
      // участник входит во встречу и слушает, а устройство включит позже.
      // Выбрасывать его из комнаты из-за этого нельзя.
      const unavailable: string[] = [];

      if (intent.micEnabled) {
        try {
          await activeRoom.localParticipant.setMicrophoneEnabled(true);
        } catch {
          unavailable.push('микрофон');
        }
      }
      if (cancelled) return;

      if (intent.cameraEnabled) {
        try {
          await activeRoom.localParticipant.setCameraEnabled(true);
        } catch {
          unavailable.push('камера');
        }
      }
      if (cancelled) return;

      if (unavailable.length > 0) {
        setError(
          `Не удалось включить: ${unavailable.join(' и ')}. Проверьте доступ в настройках браузера.`,
        );
      }

      if (intent.speakerId) {
        await activeRoom.switchActiveDevice('audiooutput', intent.speakerId).catch(() => {});
      }
    })();

    return () => {
      cancelled = true;
      closing.current = true;

      ROOM_UPDATE_EVENTS.forEach((event) => activeRoom.off(event, update));
      activeRoom.off(RoomEvent.ConnectionStateChanged, onStateChanged);
      activeRoom.off(RoomEvent.DataReceived, onData);
      activeRoom.off(RoomEvent.Reconnected, onReconnected);
      activeRoom.off(RoomEvent.RoomMetadataChanged, onRoomMetadata);
      // Слушатель снимается ДО disconnect: иначе собственное отключение
      // вернётся к нам как «связь потеряна».
      activeRoom.off(RoomEvent.Disconnected, onDisconnected);

      activeRoom.disconnect();
      setRoom((current) => (current === activeRoom ? null : current));
    };
  }, [intent, initialCodec]);

  // Сообщение об ошибке — уведомление, а не состояние: гасим его само.
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(timer);
  }, [error]);

  /**
   * Наблюдение за связью.
   *
   * ── Почему здесь больше нет автосмены видеокодека ───────────────────
   *
   * Она приносила вред. Кодек меняется только через перепубликацию
   * дорожки, а это заметный обрыв картинки у всех участников. Хуже того,
   * советчик реагировал на потери пакетов — но в групповом звонке они
   * возникают от нагрузки на приём, и понижение своего кодека отправки
   * ничем не помогало. Дойдя до лимита переключений, он замолкал, и
   * качество оставалось ухудшенным до перезагрузки страницы.
   *
   * Подстройка под сеть — это работа simulcast, dynacast и встроенного
   * в WebRTC контроля перегрузки. Они делают то же самое непрерывно,
   * плавно и в обе стороны. Своя надстройка сверху им только мешала.
   *
   * Статистика по-прежнему снимается — она видна в панели качества.
   * Битрейт звука подстраивается: он меняется через `setParameters`
   * без перепубликации и без разрыва звука.
   */
  useEffect(() => {
    if (!room) return;
    if (state !== ConnectionState.Connected) return;

    audioAdvisorRef.current ??= new AudioAdvisor();
    let stopped = false;

    const tick = async () => {
      const visible = statsVisible;

      // Панель закрыта и звук не в автоматическом режиме — пробу снимать
      // не для кого. Это единственный периодический опрос во встрече,
      // и в таком состоянии он не делает вообще ничего полезного.
      if (!visible && !audioAutoRef.current) return;

      const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
      const track = publication?.track as LocalVideoTrack | undefined;
      const sender = track?.sender;

      // Камера выключена — измерять нечего.
      if (!track || !sender || publication?.isMuted) {
        if (visible) setLinkSample(null);
        return;
      }

      const sample = await sampleLink(sender);
      if (stopped || !sample) return;
      if (visible) setLinkSample(sample);

      if (audioAutoRef.current) {
        const advice = audioAdvisorRef.current?.consider(sample);
        if (advice) {
          const applied = await applyAudioBitrate(room, advice.maxBitrate);
          if (applied && !stopped) {
            audioBitrateRef.current = advice.maxBitrate;
            setAudioBitrate(advice.maxBitrate);
            setAudioChange(advice.label);
          }
        }
      }
    };

    /*
     * Панель только что открыли — не заставляем ждать полный интервал.
     *
     * Битрейт считается по приросту байт между двумя пробами, поэтому
     * первая проба цифру дать не может в принципе. Раньше человек видел
     * «измеряем…» три секунды, а число — только через шесть. Здесь первая
     * проба снимается сразу, вторая — почти сразу за ней, и цифры
     * появляются примерно через секунду.
     */
    const warmup: ReturnType<typeof setTimeout>[] = [];
    if (statsVisible) {
      void tick();
      warmup.push(setTimeout(() => void tick(), WARMUP_INTERVAL_MS));
    }

    const timer = setInterval(tick, SAMPLE_INTERVAL_MS);
    return () => {
      stopped = true;
      warmup.forEach(clearTimeout);
      clearInterval(timer);
    };
  }, [room, state, statsVisible]);

  // ── Действия ──────────────────────────────────────────────────────
  const guard = useCallback(async (action: () => Promise<unknown>, fallback: string) => {
    try {
      await action();
    } catch (err) {
      setError(err instanceof Error ? err.message : fallback);
    }
  }, []);

  /**
   * Переключение устройства с мгновенным откликом.
   *
   * Раньше кнопка ждала, пока SDK действительно отключит микрофон или
   * камеру: состояние читалось прямо из `localParticipant`, а перерисовка
   * шла по событию комнаты, которое приходит уже после всей работы —
   * остановки дорожки, снятия публикации, пересогласования. Для камеры
   * это доли секунды, а иногда и больше, и всё это время нажатая кнопка
   * выглядела так, будто нажатие не сработало.
   *
   * Теперь желаемое состояние показывается сразу, а настоящее подменяет
   * его, как только SDK договорит. Если операция не удалась — например,
   * в доступе к камере отказано, — намерение снимается и кнопка честно
   * возвращается в прежнее положение.
   */
  const toggleDevice = useCallback(
    async (
      kind: 'mic' | 'camera',
      apply: (participant: LocalParticipant, next: boolean) => Promise<unknown>,
      current: (participant: LocalParticipant) => boolean,
      device: DeviceName,
    ) => {
      if (!room) return;
      const participant = room.localParticipant;
      const next = !current(participant);

      setPending((p) => ({ ...p, [kind]: next }));
      try {
        await apply(participant, next);
      } catch (err) {
        setError(describeDeviceError(err, device));
      } finally {
        // Снимаем намерение независимо от исхода: дальше показываем то,
        // что на самом деле произошло с устройством.
        setPending((p) => {
          const rest = { ...p };
          delete rest[kind];
          return rest;
        });
      }
    },
    [room],
  );

  const toggleMic = useCallback(
    () =>
      toggleDevice(
        'mic',
        (p, next) => p.setMicrophoneEnabled(next),
        (p) => p.isMicrophoneEnabled,
        'микрофону',
      ),
    [toggleDevice],
  );

  const toggleCamera = useCallback(
    () =>
      toggleDevice(
        'camera',
        (p, next) => p.setCameraEnabled(next),
        (p) => p.isCameraEnabled,
        'камере',
      ),
    [toggleDevice],
  );

  const toggleScreenShare = useCallback(
    () =>
      room
        ? guard(async () => {
            const on = room.localParticipant.isScreenShareEnabled;
            setNotice('');
            try {
              await room.localParticipant.setScreenShareEnabled(!on, SCREEN_SHARE_OPTIONS);
            } catch (err) {
              // Пользователь закрыл системный диалог выбора окна — это не ошибка.
              // Сравниваем по имени, а не через instanceof: SDK оборачивает
              // ошибки в свои классы, и проверка на DOMException не срабатывала.
              if ((err as { name?: string })?.name === 'NotAllowedError') return;
              throw err;
            }

            if (on) return;

            const publication = room.localParticipant.getTrackPublication(
              Track.Source.ScreenShare,
            );
            setNotice(screenShareHint(publication?.track?.mediaStreamTrack));

            /*
             * Приоритет деградации назначаем сразу, а не только при смене
             * режима качества.
             *
             * У публикации он один на всех дорожках и берётся из умолчаний
             * комнаты, то есть от камеры. В режиме «Высокое» это
             * `maintain-resolution` — и показ экрана начинал дёргаться
             * с первой же секунды, ещё до того, как человек что-то
             * переключит. Здесь ставим экрану его собственный, см.
             * `video.ts`.
             */
            const profile = videoProfileFor(videoModeRef.current);
            await applyBudget(
              room,
              Track.Source.ScreenShare,
              profile.screenShareBitrate,
              profile.screenShareDegradation,
            );
          }, 'Не удалось начать демонстрацию экрана')
        : Promise.resolve(),
    [room, guard],
  );

  const switchDevice = useCallback(
    (kind: MediaDeviceKind, deviceId: string) =>
      room
        ? guard(() => room.switchActiveDevice(kind, deviceId), 'Не удалось сменить устройство')
        : Promise.resolve(),
    [room, guard],
  );

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || !room) return;

      await guard(() => sendChatMessage(room, trimmed), 'Сообщение не отправлено');

      setChat((history) => [
        ...history,
        {
          id: `own-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          from: intent.displayName,
          text: trimmed,
          at: Date.now(),
          own: true,
        },
      ]);
    },
    [room, guard, intent.displayName],
  );

  const markChatRead = useCallback(() => setUnreadChat(0), []);

  /**
   * Смена режима звука на живой дорожке.
   *
   * Здесь намеренно НЕ используется `restartTrack`. Внутри он сначала
   * останавливает текущую дорожку и только потом запрашивает новую, —
   * если второй шаг не удался (занятый микрофон, отказ в доступе,
   * неподдерживаемые ограничения), звук пропадает совсем и обратным
   * переключением уже не чинится. Именно так терялся голос при смене
   * режима.
   *
   * `applyConstraints` меняет параметры уже захваченной дорожки, ничего
   * не останавливая: в худшем случае часть настроек не применится, но
   * человека будет слышно. Битрейт правится через `setParameters` —
   * это не трогает саму дорожку и не требует пересогласования.
   */
  const applyAudioSettings = useCallback(
    async (mode: AudioMode, suppression: boolean) => {
      audioAutoRef.current = mode === 'auto';

      const target = audioPresetFor(mode).maxBitrate ?? 24_000;

      // Автоматика начинает заново от ступени, соответствующей режиму.
      audioAdvisorRef.current = mode === 'auto' ? new AudioAdvisor() : null;
      audioBitrateRef.current = target;
      setAudioBitrate(target);
      setAudioChange('');

      if (!room) return;

      const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
      const track = publication?.track as LocalAudioTrack | undefined;

      if (track?.mediaStreamTrack) {
        try {
          await track.mediaStreamTrack.applyConstraints(liveConstraints(suppression));
        } catch {
          // Браузер не умеет менять обработку на лету (встречается на
          // WebKit). Это не повод рвать связь — режим применится при
          // следующем входе, а сейчас важнее сохранить звук.
        }
      }

      await applyAudioBitrate(room, target);
    },
    [room],
  );

  /**
   * Смена качества видео.
   *
   * Делает две вещи. Во-первых, кладёт новые настройки в умолчания
   * публикации комнаты — чтобы следующая перепубликация дорожки (выключили
   * и включили камеру, начали показ экрана) взяла уже их. Во-вторых,
   * правит потолок у живой дорожки, чтобы эффект был виден сразу, без
   * перепубликации и обрыва картинки.
   *
   * Раньше второе делалось ещё и при входе, на каждую публикацию — и это
   * ломало качество через раз, см. `layerBudgetFor`. Теперь при входе слои
   * задаются сразу правильными, а `setParameters` вызывается только по
   * осознанному действию человека, когда dynacast уже давно отработал.
   */
  const setVideoMode = useCallback(
    async (mode: VideoMode) => {
      setVideoModeState(mode);
      videoModeRef.current = mode;
      if (!room) return;

      // Умолчания публикации правим всегда: их возьмёт любая следующая
      // публикация дорожки — и наша ниже, и та, что случится сама после
      // переподключения или перевключения камеры.
      const profile = videoProfileFor(mode);
      const publish = room.options.publishDefaults;
      if (publish) Object.assign(publish, videoPublishOptions(initialCodec, mode));
      const capture = room.options.videoCaptureDefaults;
      if (capture) capture.resolution = profile.resolution;

      await guard(
        () => applyVideoProfile(room, mode, initialCodec),
        'Не удалось сменить качество видео',
      );
    },
    [room, initialCodec, guard],
  );

  const setAudioMode = useCallback(
    async (mode: AudioMode) => {
      setAudioModeState(mode);
      await guard(() => applyAudioSettings(mode, noiseSuppression), 'Не удалось сменить режим звука');
    },
    [applyAudioSettings, noiseSuppression, guard],
  );

  const setNoiseSuppression = useCallback(
    async (enabled: boolean) => {
      setNoiseSuppressionState(enabled);
      await guard(
        () => applyAudioSettings(audioMode, enabled),
        'Не удалось переключить шумоподавление',
      );
    },
    [applyAudioSettings, audioMode, guard],
  );

  /**
   * Включает или выключает режим презентации для всей комнаты.
   *
   * Состояние не ставим локально: ждём, пока сервер запишет метаданные и
   * LiveKit разошлёт событие. Иначе у организатора режим включился бы,
   * а у остальных нет — и он бы об этом не узнал.
   */
  const setPresentation = useCallback(
    async (on: boolean) => {
      try {
        await api.setPresentation(intent.credentials.room, on);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Не удалось переключить режим презентации');
      }
    },
    [intent.credentials.room],
  );

  const dismissNotice = useCallback(() => setNotice(''), []);

  const leave = useCallback(async () => {
    closing.current = true;
    await room?.disconnect();
    onLeaveRef.current('user');
  }, [room]);

  // Список пересобирается только на событиях комнаты, а не на каждый рендер —
  // иначе тикающий таймер встречи перерисовывал бы все плитки раз в секунду.
  const participants = useMemo(() => {
    const list = room ? collectParticipants(room) : [];
    if (pending.mic === undefined && pending.camera === undefined) return list;

    // Своя плитка тоже показывает намерение сразу: заглушка «камера
    // выключена» и перечёркнутый микрофон появляются в тот же миг, что
    // и новое состояние кнопки, а не через полсекунды после неё.
    return list.map((p) =>
      p.isLocal
        ? {
            ...p,
            micEnabled: pending.mic ?? p.micEnabled,
            cameraEnabled: pending.camera ?? p.cameraEnabled,
          }
        : p,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, version, pending.mic, pending.camera]);

  const video = useMemo<VideoStatus>(
    () => ({ mode: videoMode, maxBitrate: videoProfileFor(videoMode).maxBitrate }),
    [videoMode],
  );

  const audio = useMemo<AudioStatus>(
    () => ({
      mode: audioMode,
      noiseSuppression,
      bitrate: audioBitrate,
      lastChange: audioChange,
    }),
    [audioMode, noiseSuppression, audioBitrate, audioChange],
  );

  const codec = useMemo<CodecStatus>(
    () => ({
      active: initialCodec,
      auto: intent.codec === 'auto',
      sample: linkSample,
    }),
    [initialCodec, intent.codec, linkSample],
  );

  return {
    room,
    state,
    participants,
    chat,
    unreadChat,
    error,
    notice,
    dismissNotice,
    startedAt,
    codec,
    video,
    audio,

    // Намерение человека важнее фактического состояния ровно до тех пор,
    // пока SDK его выполняет: кнопка должна отзываться на нажатие сразу.
    micEnabled: pending.mic ?? room?.localParticipant.isMicrophoneEnabled ?? intent.micEnabled,
    cameraEnabled:
      pending.camera ?? room?.localParticipant.isCameraEnabled ?? intent.cameraEnabled,
    screenShareEnabled: room?.localParticipant.isScreenShareEnabled ?? false,

    isHost: intent.credentials.host === true,
    presentation,

    toggleMic,
    toggleCamera,
    toggleScreenShare,
    switchDevice,
    setVideoMode,
    setAudioMode,
    setNoiseSuppression,
    setPresentation,
    sendMessage,
    markChatRead,
    leave,
  };
}

/**
 * Меняет потолок битрейта и приоритет деградации видео без перепубликации.
 *
 * У simulcast слоёв несколько, у SVC — одна запись с `scalabilityMode`;
 * в обоих случаях потолок ставится на каждую запись, а для SVC он и так
 * считается сразу за все слои.
 */
async function applyVideoProfile(
  room: Room,
  mode: VideoMode,
  codec: VideoCodec,
): Promise<void> {
  const profile = videoProfileFor(mode);

  const resized = await applyCaptureResolution(room, profile.resolution);

  /*
   * Разрешение изменилось — публикуем дорожку заново.
   *
   * Иначе смена «на максимум» не давала видимого результата: кадр
   * действительно становился крупнее, но список слоёв сервер получает
   * ровно один раз, при публикации, и продолжал считать верхний слой
   * прежним. Зрители запрашивали качество по старой таблице и разницы
   * не видели — со стороны это выглядело как «нажал, и ничего».
   *
   * Перепубликация переигрывает список. Камеру при этом не трогаем:
   * публикуется тот же самый `MediaStreamTrack`, поэтому устройство не
   * переоткрывается и индикатор не мигает. У собеседников картинка
   * замирает на доли секунды — приемлемая цена за то, что осознанное
   * переключение наконец срабатывает.
   */
  if (resized) {
    await republishCamera(room, codec, mode);
    return;
  }

  await Promise.all([
    applyBudget(room, Track.Source.Camera, profile.maxBitrate, profile.degradation),
    // У демонстрации экрана свой бюджет и свой приоритет деградации —
    // почему он отличается от камеры, разобрано в `video.ts`.
    applyBudget(
      room,
      Track.Source.ScreenShare,
      profile.screenShareBitrate,
      profile.screenShareDegradation,
    ),
  ]);
}

/**
 * Меняет разрешение съёмки у живой дорожки камеры.
 *
 * Через `applyConstraints`, а не через `restartTrack`: тот сначала
 * останавливает дорожку и только потом запрашивает новую, и любой сбой
 * оставляет участника без камеры. Здесь в худшем случае разрешение не
 * поменяется — картинка останется прежней.
 *
 * `ideal`, а не `exact`: половина ноутбучных камер физически не умеет
 * 1080p, и жёсткое требование просто уронило бы запрос. Поэтому и ответ
 * возвращается по факту, а не по намерению: сообщаем, изменился ли
 * размер кадра на самом деле — от этого зависит, нужна ли перепубликация.
 */
async function applyCaptureResolution(
  room: Room,
  resolution: { width: number; height: number },
): Promise<boolean> {
  const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const media = (publication?.track as LocalVideoTrack | undefined)?.mediaStreamTrack;
  if (!media) return false;

  const before = media.getSettings();
  if (before.width === resolution.width && before.height === resolution.height) return false;

  try {
    await media.applyConstraints({
      width: { ideal: resolution.width },
      height: { ideal: resolution.height },
    });
  } catch {
    // Камера не умеет — остаёмся на том, что есть.
    return false;
  }

  const after = media.getSettings();
  return after.width !== before.width || after.height !== before.height;
}

/**
 * Публикует дорожку камеры заново, чтобы сервер получил новый список слоёв.
 *
 * Тот же `MediaStreamTrack` снимается с публикации и публикуется снова —
 * устройство не переоткрывается. Параметры берутся из умолчаний комнаты,
 * которые к этому моменту уже соответствуют выбранному режиму, поэтому
 * дописывать битрейт через `setParameters` не нужно: как раз этим мы
 * когда-то и ломали качество, соревнуясь с dynacast.
 */
async function republishCamera(room: Room, codec: VideoCodec, mode: VideoMode): Promise<void> {
  const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
  const track = publication?.track as LocalVideoTrack | undefined;
  if (!track) return;

  await room.localParticipant.unpublishTrack(track, false);
  await room.localParticipant.publishTrack(track, {
    source: Track.Source.Camera,
    videoCodec: codec,
    simulcast: true,
    ...videoPublishOptions(codec, mode),
  });
}

/**
 * Раскладывает бюджет по слоям одной дорожки.
 *
 * ── Почему здесь перечитывание и повтор ─────────────────────────────
 *
 * Спецификация WebRTC разрешает применить только те параметры, которые
 * прочитаны последним вызовом `getParameters` у этого же отправителя.
 * А в те же параметры пишет dynacast, включая и выключая слои по мере
 * того, кто на кого смотрит. Если между нашим чтением и записью он успеет
 * вклиниться, наш вызов упадёт — и наоборот.
 *
 * Поэтому: читаем и пишем без единого `await` между ними (чтобы окно было
 * минимальным), а при отказе перечитываем и пробуем снова. Перечитывание
 * заодно сохраняет чужие правки: свежие параметры уже содержат актуальные
 * флаги `active`, и мы их не затираем.
 */
async function applyBudget(
  room: Room,
  source: Track.Source,
  budget: number,
  degradation: RTCDegradationPreference,
): Promise<void> {
  const publication = room.localParticipant.getTrackPublication(source);
  const sender = (publication?.track as LocalVideoTrack | undefined)?.sender;
  if (!sender) return;

  for (let attempt = 0; attempt < 3; attempt++) {
    const params = sender.getParameters();
    if (!params.encodings?.length) return;

    const shares = distributeBudget(
      params.encodings.map((encoding) => encoding.scaleResolutionDownBy),
      budget,
    );

    params.degradationPreference = degradation;
    params.encodings.forEach((encoding, i) => {
      encoding.maxBitrate = shares[i];
    });

    try {
      await sender.setParameters(params);
      return;
    } catch {
      // Скорее всего в параметры только что писал dynacast. Ждём, пока он
      // договорит, и пробуем ещё раз с уже свежими значениями.
      await new Promise((resolve) => setTimeout(resolve, 120));
    }
  }
}

/**
 * Меняет потолок битрейта звука без перепубликации дорожки.
 * Возвращает false, если отправитель ещё не готов.
 */
async function applyAudioBitrate(room: Room, maxBitrate: number): Promise<boolean> {
  const publication = room.localParticipant.getTrackPublication(Track.Source.Microphone);
  const sender = (publication?.track as LocalAudioTrack | undefined)?.sender;
  if (!sender) return false;

  const params = sender.getParameters();
  if (!params.encodings?.length) return false;

  params.encodings.forEach((encoding) => {
    encoding.maxBitrate = maxBitrate;
  });

  try {
    await sender.setParameters(params);
    return true;
  } catch {
    return false;
  }
}
